/** SUNTRONIX ONE — OUR TECHNOLOGY Command Centre (tenant-facing home).
 *  Premium, mobile-first. Reuses authoritative data (tenant-scoped, owner-safe).
 *  Answers: What do we have · What is happening · What needs attention · What next.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles, Boxes, Users, Truck, LifeBuoy, ShieldCheck, RefreshCw, FileText,
  ChevronRight, AlertTriangle, Send, Loader2, Globe, MapPin, Plus, Rocket,
  PackageCheck, Wrench, Recycle,
} from "lucide-react";
import { CustomerActionsPanel } from "@/pages/customer/CustomerActions";

const REQUESTS = [
  { key: "NEW_DEVICE", label: "New device", icon: Rocket },
  { key: "REPLACEMENT", label: "Replacement", icon: RefreshCw },
  { key: "ACCESSORY", label: "Accessory", icon: Boxes },
  { key: "SERVICE", label: "Service", icon: Wrench },
  { key: "PICKUP", label: "Pickup", icon: Truck },
  { key: "RECOVERY", label: "Recovery", icon: Recycle },
  { key: "APPLECARE", label: "AppleCare", icon: ShieldCheck },
  { key: "DEPLOYMENT", label: "Deployment", icon: PackageCheck },
];

export default function CommandCentre() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [openCountry, setOpenCountry] = useState(null);
  const [lifecycle, setLifecycle] = useState([]);

  useEffect(() => {
    api.get("/our-technology/home").then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load your command centre"))
      .finally(() => setLoading(false));
    api.get("/tenant/lifecycle").then(({ data }) => setLifecycle(data.stages || [])).catch(() => {});
  }, []);

  const ask = async () => {
    if (!q.trim()) return;
    setAsking(true); setAnswer(null);
    try {
      const { data } = await api.post("/our-technology/ask", { question: q });
      setAnswer(data.answer);
    } catch { toast.error("Ask ONE is unavailable right now"); }
    finally { setAsking(false); }
  };

  const goRequest = (key) => {
    setReqOpen(false);
    nav(`/service-hub?request=${key}`);
  };

  if (loading) return <div className="p-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading your command centre…</div>;
  if (!data) return null;
  const b = data.brief;

  return (
    <div className="min-h-full bg-slate-50" data-testid="command-centre">
      {/* Hero */}
      <div className="bg-slate-900 text-white px-4 md:px-8 pt-8 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #6366f1 0, transparent 40%), radial-gradient(circle at 80% 30%, #0ea5e9 0, transparent 45%)" }} />
        <div className="relative max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-[12px] text-slate-300"><Sparkles className="h-4 w-4 text-indigo-300" /> Our Technology Command Centre
            {data.is_demo && <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-200 text-[10px] font-bold tracking-wide" data-testid="demo-badge">DEMO ENVIRONMENT</span>}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1 text-white" style={{ fontFamily: "Manrope, sans-serif", color: "#fff" }}>Everything, in control.</h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-[12px]">
            {[["What do we have", `${b.assets} assets · ${b.employees} people`],
              ["What is happening", `${b.shipments_in_transit} in transit · ${b.deployments} deploying`],
              ["What needs attention", `${data.attention.length} item(s)`],
              ["What next", `${data.refresh.length} refresh candidate(s)`]].map(([q, a], i) => (
              <div key={i} className="rounded-xl bg-white/5 border border-white/10 backdrop-blur p-3" data-testid={`hero-q-${i}`}>
                <div className="text-slate-400 text-[10px] uppercase tracking-wide">{q}</div>
                <div className="text-white font-semibold mt-0.5">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 -mt-10 relative pb-12 space-y-6">
        {/* Lifecycle navigation — Procure → Recycle */}
        {lifecycle.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-3" data-testid="lifecycle-nav">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 px-1">Our technology lifecycle</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {lifecycle.map((s, i) => (
                <button key={s.stage} disabled={!s.enabled} onClick={() => nav(s.to)} data-testid={`lifecycle-${s.stage}`}
                  className={"flex-1 min-w-[92px] rounded-xl border p-2.5 text-center transition-colors " +
                    (s.enabled ? "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700" : "border-slate-100 text-slate-300 cursor-not-allowed")}>
                  <div className="text-[10px] font-bold text-indigo-400">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-[12px] font-semibold">{s.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Ask ONE */}
        <CustomerActionsPanel />
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4" data-testid="ask-one">
          <div className="text-[12px] font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-indigo-500" /> Ask ONE about our technology</div>
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder="e.g. How many assets have warranty expiring soon?" data-testid="ask-one-input" className="flex-1" />
            <Button onClick={ask} disabled={asking} data-testid="ask-one-send" className="bg-slate-900 hover:bg-slate-800">
              {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          {answer && <div className="mt-3 rounded-lg bg-indigo-50 border border-indigo-100 p-3 text-[13px] text-slate-800" data-testid="ask-one-answer">{answer}</div>}
        </div>

        {/* AI Executive Brief */}
        <div>
          <h2 className="text-[13px] font-semibold text-slate-700 mb-2">AI executive brief</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="exec-brief">
            <Stat icon={Boxes} label="Our assets" value={b.assets} tone="text-slate-900" to="/assets" nav={nav} />
            <Stat icon={Users} label="Our people" value={b.employees} tone="text-slate-900" to="/employees" nav={nav} />
            <Stat icon={Truck} label="In transit" value={b.shipments_in_transit} tone="text-sky-700" to="/my-orders" nav={nav} />
            <Stat icon={LifeBuoy} label="Open service" value={b.open_service_cases} tone="text-amber-700" to="/service-hub" nav={nav} />
            <Stat icon={ShieldCheck} label="Warranty covered" value={b.warranty_covered} tone="text-emerald-700" to="/warranty" nav={nav} />
            <Stat icon={ShieldCheck} label="AppleCare" value={b.applecare} tone="text-slate-900" to="/operations/applecare-plus" nav={nav} />
            <Stat icon={RefreshCw} label="Warranty expiring" value={b.warranty_expiring} tone="text-rose-700" />
            {data.finance && <Stat icon={FileText} label="Invoices outstanding" value={b.invoices_outstanding ?? 0} tone="text-fuchsia-700" to="/invoices" nav={nav} />}
          </div>
        </div>

        {/* Attention */}
        {data.attention.length > 0 && (
          <div>
            <h2 className="text-[13px] font-semibold text-slate-700 mb-2">What needs attention</h2>
            <div className="space-y-2" data-testid="attention-list">
              {data.attention.map((a, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 p-3" data-testid={`attention-${a.kind}`}>
                  <AlertTriangle className={"h-4 w-4 shrink-0 " + (a.severity === "warn" ? "text-amber-500" : "text-sky-500")} />
                  <span className="flex-1 text-[13px] text-slate-800">{a.title}</span>
                  <Button size="sm" variant="outline" onClick={() => a.to === "refresh" ? document.getElementById("refresh-section")?.scrollIntoView({ behavior: "smooth" }) : nav(a.to === "service" ? "/service-hub" : a.to === "shipments" ? "/my-orders" : "/assets")}>
                    {a.action}<ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Technology estate */}
          <div>
            <h2 className="text-[13px] font-semibold text-slate-700 mb-2">Our technology estate</h2>
            <div className="rounded-xl bg-white border border-slate-200 divide-y" data-testid="estate">
              {data.estate.length === 0 && <div className="p-4 text-[13px] text-slate-400">No assets mapped yet.</div>}
              {data.estate.map((c) => (
                <div key={c.country}>
                  <button onClick={() => setOpenCountry(openCountry === c.country ? null : c.country)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-slate-50 text-left" data-testid={`estate-country-${c.country}`}>
                    <Globe className="h-4 w-4 text-slate-400" />
                    <span className="flex-1 text-[13px] font-medium text-slate-800">{c.country}</span>
                    <span className="text-[12px] tabular-nums text-slate-500">{c.count}</span>
                    <ChevronRight className={"h-4 w-4 text-slate-300 transition-transform " + (openCountry === c.country ? "rotate-90" : "")} />
                  </button>
                  {openCountry === c.country && (
                    <div className="bg-slate-50 px-4 py-1.5">
                      {c.locations.map((l) => (
                        <div key={l.location} className="flex items-center gap-2 py-1 text-[12px] text-slate-600">
                          <MapPin className="h-3 w-3 text-slate-400" /><span className="flex-1">{l.location}</span>
                          <span className="tabular-nums">{l.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {data.breakdown && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                <MiniRoll title="Status" rows={data.breakdown.by_status} />
                <MiniRoll title="Lifecycle" rows={data.breakdown.by_lifecycle} />
                <MiniRoll title="Category" rows={data.breakdown.by_category} />
              </div>
            )}
          </div>

          {/* Refresh intelligence */}
          <div id="refresh-section">
            <h2 className="text-[13px] font-semibold text-slate-700 mb-2">Refresh intelligence</h2>
            <div className="rounded-xl bg-white border border-slate-200 divide-y max-h-[420px] overflow-y-auto" data-testid="refresh-list">
              {data.refresh.length === 0 && <div className="p-4 text-[13px] text-slate-400">No refresh candidates — your fleet is well within warranty.</div>}
              {data.refresh.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`refresh-${r.id}`}>
                  <RefreshCw className={"h-3.5 w-3.5 shrink-0 " + (r.days_to_warranty_end < 0 ? "text-rose-500" : "text-amber-500")} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-slate-800 truncate">{r.product || r.asset_tag || "Asset"}</div>
                    <div className="text-[11px] text-slate-400 truncate">{r.serial || "—"} · {r.reason}</div>
                  </div>
                  <span className={"text-[11px] font-semibold tabular-nums " + (r.days_to_warranty_end < 0 ? "text-rose-600" : "text-amber-600")}>
                    {r.days_to_warranty_end < 0 ? `${Math.abs(r.days_to_warranty_end)}d ago` : `${r.days_to_warranty_end}d`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Universal + Request FAB */}
      <button onClick={() => setReqOpen(true)} data-testid="smart-request-fab"
        className="fixed bottom-5 right-20 md:bottom-6 md:right-24 z-40 h-14 pl-4 pr-5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg flex items-center gap-2 font-semibold whitespace-nowrap">
        <Plus className="h-5 w-5" /> Request
      </button>
      {reqOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end md:items-center justify-center p-4" onClick={() => setReqOpen(false)} data-testid="smart-request-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5">
            <div className="text-sm font-semibold mb-1">What can ONE do for you?</div>
            <div className="text-[12px] text-slate-500 mb-4">Every request uses your existing service workflows.</div>
            <div className="grid grid-cols-2 gap-2">
              {REQUESTS.map((r) => {
                const Icon = r.icon;
                return (
                  <button key={r.key} onClick={() => goRequest(r.key)} data-testid={`request-${r.key}`}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 p-3 text-left text-[13px] font-medium text-slate-700">
                    <Icon className="h-4 w-4 text-indigo-500" />{r.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone = "text-slate-900", to, nav }) {
  const clickable = to && nav;
  return (
    <div onClick={() => clickable && nav(to)}
      className={"rounded-xl bg-white border border-slate-200 p-3 " + (clickable ? "cursor-pointer hover:border-slate-300 transition-colors" : "")}
      data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[10px] font-medium text-slate-500 flex items-center gap-1"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className={"text-[22px] font-bold mt-0.5 tabular-nums " + tone}>{value}</div>
    </div>
  );
}

function MiniRoll({ title, rows }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-2">
      <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">{title}</div>
      {(rows || []).slice(0, 4).map((r) => (
        <div key={r.label} className="flex items-center justify-between text-[11px] text-slate-600 py-0.5">
          <span className="truncate mr-1">{r.label}</span><span className="tabular-nums font-medium">{r.count}</span>
        </div>
      ))}
    </div>
  );
}
